import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { API_BASE_URL } from '@/client/config';
import { handleApiError } from '@/client/lib/apiError';
import type {
  PipelineLogEntry,
  PipelineLogsResponse,
  PipelineSearchType,
  PipelineLogType,
} from '@shared/types';

interface PipelineLogsSearchParams {
  searchType: PipelineSearchType;
  searchValue: string;
  dateFrom: string;
  dateTo?: string;
  logType?: PipelineLogType;    // single type (legacy, for contextual links)
  logTypes?: string;            // comma-separated list (for multi-select UI)
  limit?: number;
  offset?: number;
}

interface PipelineLogsState {
  // Data
  logs: PipelineLogEntry[];
  hasMore: boolean;

  // Search params (what was last searched)
  lastSearchParams: PipelineLogsSearchParams | null;

  // Metadata from API response
  meta: {
    dateFrom: string;
    dateTo: string;
    limit: number;
    offset: number;
  } | null;

  // UI state
  loading: boolean;
  error: string | null;

  // Actions
  fetchLogs: (
    accessToken: string,
    logout: () => void,
    params: PipelineLogsSearchParams
  ) => Promise<void>;
  clearLogs: () => void;
}

export const usePipelineLogsStore = create<PipelineLogsState>()(
  devtools(
    (set) => ({
      // Initial state
      logs: [],
      hasMore: false,
      lastSearchParams: null,
      meta: null,
      loading: false,
      error: null,

      fetchLogs: async (accessToken, logout, params) => {
        set({ loading: true, error: null });

        try {
          const searchParams = new URLSearchParams();
          searchParams.set('searchType', params.searchType);
          searchParams.set('searchValue', params.searchValue);
          searchParams.set('dateFrom', params.dateFrom);
          if (params.dateTo) searchParams.set('dateTo', params.dateTo);
          // Prefer logTypes (multi-select) over logType (single)
          if (params.logTypes) {
            searchParams.set('logTypes', params.logTypes);
          } else if (params.logType) {
            searchParams.set('logType', params.logType);
          }
          if (params.limit) searchParams.set('limit', params.limit.toString());
          if (params.offset) searchParams.set('offset', params.offset.toString());

          const url = `${API_BASE_URL}/api/pipeline/logs?${searchParams}`;
          const response = await fetch(url, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            if (handleApiError(response, logout)) return;
            throw new Error(`Failed to fetch pipeline logs: ${response.statusText}`);
          }

          const data: PipelineLogsResponse = await response.json();

          set({
            logs: data.logs,
            hasMore: data.meta.hasMore,
            lastSearchParams: params,
            meta: {
              dateFrom: data.meta.dateFrom,
              dateTo: data.meta.dateTo,
              limit: data.meta.limit,
              offset: data.meta.offset,
            },
            loading: false,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
          set({ error: errorMessage, loading: false });
        }
      },

      clearLogs: () => {
        set({
          logs: [],
          hasMore: false,
          lastSearchParams: null,
          meta: null,
          error: null,
        });
      },
    }),
    { name: 'pipeline-logs-store' }
  )
);
